import { Vector3, Quaternion, Matrix4 } from "../../../imports/avm";
import { VisibilityState } from "../MeshCollectionBase";

// Shared temp for composing local transforms in updateTransformWorld.
const _tempLocalMatrix = new Matrix4();
// Shared temp for reading parent world matrix in updateTransformWorld (external call fallback).
const _tempParentMatrix = new Matrix4();

/**
 * Basic node type for the scene graph.
 */
export class Node3D {
  /** @type {Node3D | null} */
  parent = null;
  /** @type {Node3D[]} */
  children = [];

  /** @type {Vector3} */
  position = new Vector3(0, 0, 0);
  /** @type {Quaternion} */
  quaternion = new Quaternion(0, 0, 0, 1);
  /** @type {Vector3} */
  scale = new Vector3(1, 1, 1);

  /** @type {boolean} Flag indicating this node's world matrix needs update. */
  _dirty = true;

  /** @type {boolean} Flag indicating some descendant has _dirty=true (for subtree skipping). */
  _subtreeDirty = true;

  /** @type {MeshCollectionBase|null} */
  _meshCollection = null;

  /**
   * Temporary state for this node. Inheriting classes can use this to store temporary data.
   * It will be cleared after initialization if another datasource e.g. a mesh collection is available.
   */
  _detachedState = {
    /** @type {VisibilityState} */
    visibilityState: VisibilityState.Visible
  };

  /**
   * @param {boolean} [skipOwnMatrixWorld=false] Internal: if true, skip allocating the
   *   matrixWorld own property. Used by MeshNode3D which does not store its own world matrix.
   */
  constructor(skipOwnMatrixWorld = false) {
    if (!skipOwnMatrixWorld) {
      /** @type {Matrix4} Represents the object's transformation matrix in world space. */
      this._matrixWorld = new Matrix4();
    }
  }

  /**
   * Computes the local transform matrix for this node.
   * @param {Matrix4} [target] Matrix to write into.
   * @returns {Matrix4} The target filled with the local matrix, or a new matrix if no target is provided.
   */
  getTransformLocal(target) {
    target = target || new Matrix4();
    target.compose(this.position, this.quaternion, this.scale);
    return target;
  }

  /**
   * Reads the world transform matrix into {@link target}.
   * If no target is provided, returns a new matrix.
   * Subclasses (e.g. MeshNode3D) override this to read from external storage.
   *
   * @param {Matrix4} [target] Matrix to write into.
   * @returns {Matrix4} The target filled with the world matrix, or this._matrixWorld.
   */
  getTransformWorld(target) {
    return target ? target.copy(this._matrixWorld) : this._matrixWorld.clone();
  }

  /**
   * Sets the visibility state for this node and its children.
   * @param {VisibilityState} value
   * @returns {void}
   */
  setVisibilityState(value) {
    if (this._detachedState) {
      this._detachedState.visibilityState = value;
    }
    for (let idx = 0; idx < this.children.length; idx++) {
      this.children[idx].setVisibilityState(value);
    }
  }

  /**
   * @returns {VisibilityState} The visibility state.
   */
  getVisibilityState() {
    return this._detachedState.visibilityState;
  }

  /**
   * Marks this node as needing a world matrix update.
   * Call this after modifying position, quaternion, or scale when using dirty tracking.
   * @returns {void}
   */
  markDirty() {
    if (!this._dirty) {
      this._dirty = true;
      this._subtreeDirty = true;

      // Propagate subtreeDirty up to ancestors (O(depth))
      let ancestor = this.parent;
      while (ancestor && !ancestor._subtreeDirty) {
        ancestor._subtreeDirty = true;
        ancestor = ancestor.parent;
      }
    }
  }

  /**
   * Updates world matrix and applies it to the represented fragment.
   * Computations are performed immediately and propagated to all descendants.
   * Uses subtree skipping optimization: if a subtree has no dirty nodes, it's skipped entirely.
   * @param {boolean} [force=false] If true, forces update regardless of dirty flag.
   * @param {Matrix4} [_parentWorldMatrix=null] Internal: parent's world matrix passed during recursion.
   *   Falls back to this.parent.getTransformWorld() when called externally.
   * @returns {void}
   */
  updateTransformWorld(force = false, _parentWorldMatrix = null) {
    // Check if this node needs updating (dirty or forced)
    const needsUpdate = this._dirty || force;

    if (needsUpdate) {
      this.getTransformLocal(_tempLocalMatrix);

      const parentWorld = _parentWorldMatrix || this.parent?.getTransformWorld?.(_tempParentMatrix);
      if (parentWorld) {
        this._matrixWorld.multiplyMatrices(parentWorld, _tempLocalMatrix);
      } else {
        this._matrixWorld.copy(_tempLocalMatrix);
      }

      // Clear dirty flag after update
      this._dirty = false;
    }

    // Only traverse children if this node was updated OR subtree has dirty nodes
    // When force=false and no subtree is dirty, skip traversing children entirely (O(1) instead of O(n))
    if (needsUpdate || this._subtreeDirty) {
      for (const child of this.children) {
        child.updateTransformWorld(needsUpdate, this._matrixWorld);
      }
      // Clear subtreeDirty after processing children
      this._subtreeDirty = false;
    }
  }

  /**
   * @returns {boolean} True, if the node is attached to a model, false otherwise.
   */
  isAttached() {
    return this._meshCollection !== null;
  }

  /**
   * Detaches this node from the mesh collection.
   * @param {number[]} meshIdsToRemove - Array to gather Mesh IDs that should be removed from the mesh collection.
   * @internal
   */
  _detachFromMeshCollection(meshIdsToRemove) {
    if (this._meshCollection) {
      this._detachedState = {
        visibilityState: this.getVisibilityState()
      };

      this._meshCollection = null;
    }
  }

  /**
   * Removes children from this node and from the mesh collection.
   * @param {...Node3D|Node3D[]} children
   * @returns {this}
   */
  remove(...children) {
    const childArray = children.length === 1 && Array.isArray(children[0]) ? children[0] : children;

    // Used to collect meshes to remove from the mesh collection in one batch instead of individually. Potentially filled in derived classes like MeshNode3D.
    const meshIdsToRemove = [];
    const toRemove = new Set(childArray);

    // Detach children from the mesh collection and remove them from the parent
    for (let idx = 0; idx < childArray.length; ++idx) {
      const child = childArray[idx];
      if (child.parent === this) {
        child.traverse((n) => n._detachFromMeshCollection(meshIdsToRemove));
        child.parent = null;
      }
    }

    // In-place compaction of the children array
    let writeIdx = 0;
    for (let i = 0; i < this.children.length; i++) {
      if (!toRemove.has(this.children[i])) {
        this.children[writeIdx++] = this.children[i];
      }
    }
    this.children.length = writeIdx;

    this._meshCollection?.remove(meshIdsToRemove);

    return this;
  }

  /**
   * Removes this node from its parent and the mesh collection.
   * @returns {this}
   */
  removeFromParent() {
    if (this.parent) {
      this.parent.remove(this);
    }
    return this;
  }

  /**
   * Traverses this node and its descendants and calls the callback for each node.
   * @param {(node: Node3D) => void} callback
   * @returns {void}
   */
  traverse(callback) {
    const stack = [this];
    while (stack.length) {
      const n = stack.pop();
      // Add children to stack BEFORE calling callback, as callback may modify n.children
      if (n.children && n.children.length) {
        for (let i = n.children.length - 1; i >= 0; i--) {
          stack.push(n.children[i]);
        }
      }
      callback(n);
    }
  }

  /**
   * Sets the mesh collection for this node and propagates to all descendants.
   * @param {MeshCollectionBase} meshCollection
   * @param {boolean} [updateMatrices=true] If true, updateTransformWorld is called before child initialization.
   * @returns {this}
   */
  _initialize(meshCollection, updateMatrices = true) {
    this._meshCollection = meshCollection;

    // Update world matrices for this node and all descendants BEFORE
    // child initialization, so children have correct parent transforms
    // when they create meshes. Only called on the root node.
    if (updateMatrices) {
      this.updateTransformWorld(true);
    }

    for (const child of this.children) {
      child._initialize(meshCollection, false);
    }

    return this;
  }


  /**
   * Adds child nodes. If a child already has a parent, it will be removed from its parent.
   * If not already initialized, the child will be initialized with the mesh collection of this node.
   * @param {...Node3D|Node3D[]} children
   * @returns {this}
   */
  add(...children) {
    const childArray = children.length === 1 && Array.isArray(children[0]) ? children[0] : children;

    for (let idx = 0; idx < childArray.length; ++idx) {
      const child = childArray[idx];
      if (!child || child === this || child.parent === this)
      continue;

      if (child.parent)
      child.parent.remove(child);

      child.parent = this;

      this.children.push(child);
      if (this._meshCollection)
      child._initialize(this._meshCollection);
    }

    return this;
  }

  /**
   * Removes all children from this node.
   * @returns {this}
   */
  clear() {
    if (this.children.length > 0) {
      const meshIdsToRemove = [];
      for (let idx = 0; idx < this.children.length; idx++) {
        this.children[idx].traverse((n) => {
          n._detachFromMeshCollection(meshIdsToRemove);
        });
        this.children[idx].parent = null;
      }
      this._meshCollection?.remove(meshIdsToRemove);
      this.children.length = 0;
    }
    return this;
  }
}